import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { ChevronRight, ChevronsUpDown, LogOut } from "lucide-react";
import { Icons } from "../icons";
import dummyLogo from "@/assets/images/dummy-logo.png";
import { navOrganisation, navWorkspace } from "@/constants/data";
import { cn, getUserInitials, formatUserRole } from "@/lib/utils";
import { useAuthStore } from "@/store/auth";
import { usePostHog } from "posthog-js/react";

type UserData = {
  context: string;
  email: string;
  groupName?: string;
  organisation: any;
  workspace: any;
};

const AppSidebar: React.FC<{ userData: UserData; workspaceLogo: string }> = ({
  userData,
  workspaceLogo,
}) => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.clearAuthData);
  const location = useLocation();
  const { state } = useSidebar();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const navOptions =
    userData?.context === "workspace" ? navWorkspace : navOrganisation;

  const handleLogout = () => {
    posthog?.capture('logout_clicked', { context: userData?.context });
    logout();
    navigate("/sign-in");
  };

  // Get user role based on context
  const getUserRole = () => {
    if (userData?.context === "organisation" && user?.organisation?.role) {
      return formatUserRole(user.organisation.role);
    }
    if (userData?.context === "workspace" && user?.workspace?.role) {
      return formatUserRole(user.workspace.role);
    }
    return "";
  };

  const userRole = getUserRole();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="borer-red-500 flex items-center border">
        <div className="flex w-full items-center justify-between gap-2">
          {state !== "collapsed" && (
            <div className="flex h-10 w-3/4 items-center justify-center rounded-2xl">
              {workspaceLogo ? (
                <div className="flex justify-start">
                  <img
                    src={workspaceLogo}
                    alt="Workspace Logo"
                    className="h-10 w-[70%] object-contain"
                  />
                </div>
              ) : (
                <div className="flex justify-start">
                  <img
                    src={dummyLogo}
                    alt="Logo placeholder"
                    className="h-10 w-[70%] object-contain"
                  />
                </div>
              )}
            </div>
          )}

          <SidebarTrigger
            className={cn("-ml-1", state === "expanded" && "rotate-180")}
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="overflow-x-hidden">
        <SidebarGroup>
          <SidebarGroupLabel>Overview</SidebarGroupLabel>
          <SidebarMenu>
            {navOptions?.map((item) => {
              const Icon = item.icon ? Icons[item.icon] : Icons.logo;
              return item?.items && item?.items?.length > 0 ? (
                <Collapsible
                  key={item.title}
                  asChild
                  defaultOpen={item.isActive}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={location.pathname === item.url}
                      >
                        {item.icon && <Icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === subItem.url}
                            >
                              <Link
                                to={subItem.url}
                                onClick={() => posthog?.capture('nav_clicked', { label: subItem.title, destination: subItem.url, location: 'sidebar' })}
                              >
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem key={item.title} id={item.id}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={
                      location.pathname === item.url ||
                      location.pathname.includes(item.url)
                    }
                  >
                    <Link
                      to={item.url}
                      onClick={() => posthog?.capture('nav_clicked', { label: item.title, destination: item.url, location: 'sidebar' })}
                    >
                      <Icon />
                      <span className="text-base">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="bg-sidebar-accent text-white"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg border bg-white">
                      <div className="text-sidebar-accent">
                        {getUserInitials(user?.email || "")}
                      </div>
                    </AvatarFallback>
                  </Avatar>

                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate text-xs">
                      {user?.email || ""}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg border bg-white">
                        <div className="text-sidebar-accent">
                          {getUserInitials(user?.email || "")}
                        </div>
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {userData?.groupName ||
                          userData?.organisation?.name ||
                          ""}
                      </span>
                      <span className="truncate text-xs">
                        {user?.email || ""}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-1 py-1.5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Role:</span>
                    <span className="font-medium">{userRole}</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
